import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { formatNumber } from 'shared/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form } from '@/components/ui/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { REASON_SCHEMA } from './schemas';
import { ReasonField } from './reason-field';
import type { PrimaryEmailCounts } from './types';

const SCHEMA = z.object({ keepOldAsSecondary: z.boolean(), reason: REASON_SCHEMA });

interface ChangePrimaryPanelProps {
  oldEmail: string;
  newEmail: string;
  isNewAddress: boolean;
  counts: PrimaryEmailCounts;
  submitting: boolean;
  onSubmit: (args: { keepOldAsSecondary: boolean; reason: string }) => void;
  onCancel: () => void;
}

const countRows = (counts: PrimaryEmailCounts) => [
  ['Org memberships (the sign-in identity)', counts.subscriptions],
  ['Notifications the user sent', counts.notifications],
  ['Communication recipients', counts.communicationRecipients],
  ['Knowledge item history entries', counts.knowledgeItemRevisionLogs],
  ['Expired sign-in sessions (removed)', counts.deadConnections],
];

/**
 * Preview step of a primary email change: the records that carry the old address, the keep-as-
 * secondary choice, and the reason. Nothing is changed until the form is submitted.
 */
export function ChangePrimaryPanel({
  oldEmail,
  newEmail,
  isNewAddress,
  counts,
  submitting,
  onSubmit,
  onCancel,
}: ChangePrimaryPanelProps) {
  const form = useForm<z.infer<typeof SCHEMA>>({
    resolver: zodResolver(SCHEMA),
    defaultValues: { keepOldAsSecondary: false, reason: '' },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ keepOldAsSecondary, reason }) =>
          onSubmit({ keepOldAsSecondary, reason }),
        )}
        className="space-y-3 rounded-lg border border-primary p-4">
        <h3 className="font-semibold">Change primary email</h3>
        <div className="flex flex-wrap items-center gap-2 font-medium [overflow-wrap:anywhere]">
          <span className="font-normal text-muted-foreground line-through">{oldEmail}</span>
          <span aria-hidden="true">→</span>
          <span>{newEmail}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {isNewAddress &&
            'The new address is added as a secondary email first, then made primary. '}
          These records still carry the old address and will be updated. Nothing has changed yet.
        </p>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Where the old address appears</TableHead>
                <TableHead className="text-right">Records</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countRows(counts).map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatNumber(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={form.watch('keepOldAsSecondary')}
            onCheckedChange={checked => form.setValue('keepOldAsSecondary', checked === true)}
            disabled={submitting}
          />
          <span>
            Keep <strong>{oldEmail}</strong> as a secondary email.
            <br />
            <span className="text-muted-foreground">
              Leave this off when the old address was a typo. Turn it on when the user changed
              domains and still signs in with the old one somewhere.
            </span>
          </span>
        </label>
        <ReasonField control={form.control} disabled={submitting} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Changing...' : 'Change primary email'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
